import {
    Drawer,
    DrawerContent,
    DrawerItem,
    DrawerItemProps,
    DrawerSelectEvent,
    TabStrip,
    TabStripSelectEventArguments,
    TabStripTab,
} from '@progress/kendo-react-layout'
import { useAppContext } from 'context/AppContext'
import { useRouter } from 'next/router'
import { ReactElement, useEffect, useState } from 'react'
import { getTranslation } from 'utils/language-helper'
import Dashboard from '..'
import { DisplayTitle } from 'utils/display-title-helper'
import { useAuth } from 'context/AuthContext'
import { RoleTypes } from 'types/staff'
import { classNames } from 'utils/dom-helpers'

interface Props {
    children?: ReactElement
}

function DashboardSettings({ children }: Props) {
    const router = useRouter()
    const { translations, loadComponentTranslations } = useAppContext()
    const {
        hasRole,
    } = useAuth()
    const [selectedTab, setSelectedTab] = useState<number>(0)

    useEffect(() => {
        if (!translations?.SETTINGS_TRANSLATION_ID) {
            loadComponentTranslations('SETTINGS_TRANSLATION_ID')
        }
    }, [translations])

    const items = [
        {
            text: 'Account Information',
            descr: 'Details about your account',
            translation_key: 'settings_account_information',
            translation_descr_key: 'setting_account_description',
            icon: 'feather-user',
            selected: true,
            route: '/dashboard/settings/basic',
        },
        {
            text: 'Booking Page Settings',
            descr: 'Customizes the look of Customer Booking Page',
            translation_key: 'common.booking_page_settings',
            translation_descr_key:
                'common.booking_page_settings_description',
            icon: 'feather-edit',
            route: '/dashboard/settings/bookingPage',
        },
        {
            text: 'Calendar Options',
            descr: 'Customizes the look of the Calendar',
            translation_key: 'settings.calendar_settings',
            translation_descr_key: 'settings.calendar_setings_desc',
            icon: 'feather-calendar',
            route: '/dashboard/settings/calendar',
        },
         {
            text: 'Menu Options',
            descr: 'Customizes the menus',
            translation_key: 'settings.menu_settings',
            translation_descr_key: 'settings.menu_setings_desc',
            icon: 'feather-file-text',
            route: '/dashboard/settings/menuSettings',
            is_hidden: hasRole([RoleTypes.SUPER_ADMIN]) ? false : true
        },
        {
            text: 'Free Subscription Options',
            descr: 'Upgrades Subscriptions',
            translation_key: 'Free Subscription',
            translation_descr_key: 'Upgrades Subscriptions',
            icon: 'feather-gift',
            route: '/dashboard/settings/freeSubscription',
            is_hidden: hasRole([RoleTypes.SUPER_ADMIN]) ? false : true
        },
        {
            text: 'Languages',
            descr: 'Manage language preferences',
            translation_key: 'settings_languages',
            translation_descr_key: 'settings_languages_desc',
            icon: 'feather-globe',
            route: '/dashboard/settings/languages',
        },
        {
            text: 'Payment Policy',
            descr: 'Manage payment options',
            translation_key: 'setting_payment_policy',
            translation_descr_key: 'setting_payment_policy_description',
            icon: 'feather-briefcase',
            route: '/dashboard/settings/paymentPolicy',
        },
        {
            text: 'Security',
            descr: 'Manage your account password',
            translation_key: 'setting_security',
            translation_descr_key: 'setting_security_description',
            icon: 'feather-shield',
            route: '/dashboard/settings/security',
        },
    ]

    const CustomItem = (props: DrawerItemProps) => {
        return (
            <DrawerItem {...props}>
                <span className={'self-start k-icon ' + props.icon} />
                <div className="ml-2 w-full">
                    <span className="font-bold">{props.text}</span>
                    <span className="text-xs block">{props.descr}</span>
                </div>
            </DrawerItem>
        )
    }

    const onSelect = (e: DrawerSelectEvent) => {
        router.push(e.itemTarget.props.route)
    }

    const setSelectedItem = (path_name: string) => {
        const current_path = items.find((item) => item.route === path_name)

        if (current_path && current_path.text) {
            return current_path.text
        }
    }

    let selected: string | undefined = setSelectedItem(router.pathname)

    if (!selected) {
        selected = items[0].text
        router.push(items[0].route)
    }

    // To synch selectedTab with current route
    useEffect(() => {
        const currentTab = items.filter((item) => !item.is_hidden).findIndex(item => item.route === router.pathname)
        if (currentTab >= 0) {
            setSelectedTab(currentTab)
        }
    }, [router.pathname])

    const drawer_items = items.filter((item) => !item.is_hidden).map((item) => {
        if (item.translation_key) {
            item.text = getTranslation(item.translation_key, translations)
            item.descr = getTranslation(
                item.translation_descr_key,
                translations
            )
        }

        return {
            ...item,
            selected: router.pathname === item.route,
        }
    })
   
    // to handle tab selection
    const handleSelectTab = (e: TabStripSelectEventArguments) => {
        setSelectedTab(e.selected)
        router.push(drawer_items[e.selected].route)
    }
    
    return (
        <Dashboard>
            <div className="flex flex-col">
            <div className="py-3 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="mb-4">{DisplayTitle()}</div>
            <TabStrip
            selected={selectedTab}
            onSelect={handleSelectTab}
            className="z-20"
            animation={false}
        >
            {
                drawer_items.filter(item => !item.is_hidden) // Filter out hidden items
                .map((item, index) => {
                    return (
                        <TabStripTab
                            title={<><span className={'self-start mr-2 k-icon ' + item.icon} />{item.text}</>}
                            key={index}
                        >
                            {children}
                        </TabStripTab>
                    )
                })
            }
        </TabStrip>
        {/* <Drawer
                className="relative z-0 pb-6 sm:pb-0"
                expanded={true}
                mode={'push'}
                width={330}
                position={'start'}
                items={drawer_items}
                item={CustomItem}
                onSelect={onSelect}
            >
                <DrawerContent>{children}</DrawerContent>
            </Drawer> */}
            </div>
            </div>
        </Dashboard>
    )
}

export default DashboardSettings
